import * as Sentry from '@sentry/react';
import WhatsAppSupportLink from './shared/WhatsAppSupportLink';

// Error fallback component
function ErrorFallback({ error, resetError }) {
  // Capture exception when error boundary is triggered
  if (error) {
    Sentry.captureException(error, {
      tags: {
        errorBoundary: true,
      },
      contexts: {
        react: {
          componentStack: error.stack,
        },
      },
    });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-wine-background p-4">
      <div className="max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-xl font-bold text-wine-primary">
          Something went wrong
        </h2>
        <p className="mb-4 text-gray-600">
          We've been notified and are working on a fix. Please try refreshing the page.
        </p>
        {error && import.meta.env.MODE === 'development' && (
          <details className="mb-4">
            <summary className="cursor-pointer text-sm text-gray-500">
              Error details (dev only)
            </summary>
            <pre className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs">
              {error.toString()}
            </pre>
          </details>
        )}
        <div className="flex gap-2">
          <button
            onClick={resetError}
            className="rounded bg-wine-primary px-4 py-2 text-white hover:bg-opacity-90"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Refresh Page
          </button>
        </div>
        <p className="mt-4 text-sm text-gray-500">
          Need a human?{' '}
          <WhatsAppSupportLink error={error} className="font-medium text-wine-primary underline" />
        </p>
      </div>
    </div>
  );
}

// Create Sentry Error Boundary wrapper
export const SentryErrorBoundary = Sentry.withErrorBoundary(
  ({ children }) => children,
  {
    fallback: ({ error, resetError }) => (
      <ErrorFallback error={error} resetError={resetError} />
    ),
    beforeCapture: (scope, error, errorInfo) => {
      scope.setTag('errorBoundary', true);
      if (errorInfo) {
        scope.setContext('react', {
          componentStack: errorInfo.componentStack,
        });
      }
      // Capture exception with context
      Sentry.captureException(error, {
        tags: {
          errorBoundary: true,
        },
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    },
  }
);

